import { Input, toast } from "@medusajs/ui"
import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { PeriodLegend, TimeChart, type TimePoint } from "@/analytics/charts"
import { useCan } from "@/lib/permissions"
import { useSaveChargebackLimit, type HomeData, type MetricKey, type MidHealth, type SourceRow } from "./api"
import { count, dayLabel, dayLong, fmt, fmtAxis, hourLabel, money, noBaselineHint, pct, shortName, timeOfDay } from "./format"
import { Card, Delta, MoreLink, Muted, Row, Segmented, StatusBadge } from "./parts"

// ── Primary chart ────────────────────────────────────────────────────────────

const CHART_CHIPS: MetricKey[] = ["net_sales", "orders", "aov", "sessions", "conversion", "contribution", "new_customers"]

export function PrimaryChart({ data, metric, onMetric }: { data: HomeData; metric: MetricKey; onMetric: (k: MetricKey) => void }) {
  const def = data.definitions[metric]
  const c = data.chart
  const cur = c.current[metric] ?? []
  const prev = c.previous?.[metric] ?? null
  const hourly = c.grain === "hour"
  const points: TimePoint[] = c.keys.map((k, i) => ({
    key: k,
    label: hourly ? hourLabel(k) : dayLabel(k),
    long: hourly ? `${dayLong(data.window.since)}, ${hourLabel(k)}` : dayLong(k),
    value: cur[i] ?? null,
    prev: prev ? (prev[i] ?? null) : null,
    prevLong: prev && c.compare_keys[i] ? (hourly ? `${dayLong(data.window.compare_since!)}, ${hourLabel(c.compare_keys[i])}` : dayLong(c.compare_keys[i])) : null,
  }))
  const curLabel = hourly ? (data.window.range === "today" ? "Today" : dayLong(data.window.since)) : data.window.label
  // No comparison line against days before the launch (backend sends no previous series then).
  const prevLabel = data.window.compare_since && c.previous ? (hourly ? dayLong(data.window.compare_since) : data.window.baseline_detail ?? "Comparison") : null
  const rate = def.format === "percent" || def.format === "ratio" || metric === "aov"
  const title = `${def.label}${c.cumulative ? (rate ? ", running by hour" : ", cumulative by hour") : ", by day"}`
  return (
    <Card title={title} right={<PeriodLegend current={curLabel} previous={prevLabel} />} className="h-full">
      <div data-testid="primary-chart">
        <TimeChart points={points} format={(v) => fmt(def, v)} axisFormat={fmtAxis(def.format)} kind="line" height={236} currentLabel={curLabel} previousLabel={prevLabel} gaps />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Chart metric">
        {CHART_CHIPS.map((k) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={k === metric}
            onClick={() => onMetric(k)}
            className={`h-7 rounded-[var(--k-r-sm)] px-2.5 text-[13px] transition-colors ${k === metric ? "bg-[var(--k-blue-50)] font-medium text-[var(--k-blue)] shadow-[inset_0_0_0_1px_var(--k-blue)]" : "bg-[var(--k-sunken)] text-[var(--k-ink-muted)] shadow-[inset_0_0_0_1px_var(--k-line)] hover:text-[var(--k-ink)]"}`}
          >
            {data.definitions[k].short ?? data.definitions[k].label}
          </button>
        ))}
      </div>
    </Card>
  )
}

// ── Checkout funnel ──────────────────────────────────────────────────────────

const STEP_VERB: Record<string, string> = { cart: "add to cart", checkout: "start checkout", purchased: "complete" }

export function Funnel({ data }: { data: HomeData }) {
  const f = data.funnel
  const top = Math.max(f.steps[0]?.value ?? 0, 1)
  const checkout = f.steps.find((s) => s.key === "checkout")?.value ?? 0
  const bought = f.steps.find((s) => s.key === "purchased")?.value ?? 0
  const dropPct = checkout > 0 ? Math.round(100 - (100 * bought) / checkout) : null
  return (
    <Card
      title="Checkout funnel"
      right={<MoreLink to="/analytics/reports">Reports</MoreLink>}
      className="h-full"
      footer={
        f.available ? (
          <>
            {dropPct != null ? `${dropPct}% of checkouts drop. ` : ""}
            {f.declines ? (
              <>
                {count(f.declines)} card decline{f.declines === 1 ? "" : "s"} in this period, see{" "}
                <a href="#payments" className="text-[var(--k-blue)] hover:underline">
                  Payments
                </a>
                .
              </>
            ) : (
              "No card declines in this period."
            )}
          </>
        ) : undefined
      }
    >
      {!f.available ? (
        <div className="flex h-full min-h-[200px] flex-col items-start justify-center gap-2">
          <p className="text-[14px] font-medium text-[var(--k-ink)]">No storefront sessions in this period</p>
          <Muted>The funnel fills in from Keel's own storefront events (page views, add to cart, checkout, purchase) as shoppers visit.</Muted>
        </div>
      ) : (
        <ol className="flex flex-col" data-testid="funnel">
          {f.steps.map((s, i) => {
            const conv = i > 0 ? f.conversions[i - 1] : null
            const weakest = conv && f.weakest === s.key
            return (
              <li key={s.key}>
                {conv && (
                  <div className={`my-1.5 flex items-center gap-1.5 pl-1 text-[13px] ${weakest ? "font-medium text-[var(--k-alert)]" : "text-[var(--k-ink-muted)]"}`}>
                    <span aria-hidden>↓</span>
                    <span className="k-num">{conv.rate == null ? "—" : pct(conv.rate, 1)}</span>
                    <span>{STEP_VERB[s.key]}</span>
                    {weakest && <span>· weakest step</span>}
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[14px] text-[var(--k-ink)]">{s.label}</span>
                  <span className="k-data text-[14px] text-[var(--k-ink)]">{count(s.value)}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--k-sunken)] shadow-[inset_0_0_0_1px_var(--k-line)]">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(1.5, (100 * s.value) / top)}%`, background: i === f.steps.length - 1 ? "var(--k-positive)" : i === 0 ? "var(--k-hull)" : "var(--k-tide)" }} />
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </Card>
  )
}

// ── Where the money went ─────────────────────────────────────────────────────

export function Waterfall({ data }: { data: HomeData }) {
  const [mode, setMode] = useState<"usd" | "pct">("usd")
  const w = data.waterfall
  const g = w.gross_sales
  const rows: { key: string; label: string; amount: number; kind: "total" | "minus" | "result"; hint?: string }[] = [
    { key: "gross", label: "Gross sales", amount: g, kind: "total" },
    { key: "discounts", label: "Discounts", amount: -w.discounts, kind: "minus" },
    {
      key: "refunds",
      label: "Refunds",
      amount: -w.refunds,
      kind: "minus",
      hint: `${data.definitions.refunds.definition} ${count(w.refund_detail.refunds)} refund${w.refund_detail.refunds === 1 ? "" : "s"} ${money(w.refund_detail.refunded)}, ${count(w.refund_detail.cancellations)} cancellation${w.refund_detail.cancellations === 1 ? "" : "s"} ${money(w.refund_detail.cancelled)}.`,
    },
    { key: "fees", label: "Processor fees", amount: -w.processor_fees, kind: "minus", hint: `Order fees ${money(w.fees.order_fees)}, refund fees ${money(w.fees.refund_fees)}, chargeback fees ${money(w.fees.chargeback_fees)}, card checks ${money(w.fees.card_checks)}, fixed costs ${money(w.fees.fixed)}, fees given back ${money(-w.fees.fees_given_back)}${w.fees.true_up ? `, true-up ${money(w.fees.true_up)}` : ""}` },
    { key: "cogs", label: "COGS", amount: -w.cogs, kind: "minus" },
    { key: "ship", label: "Shipping & packaging", amount: -w.shipping_packaging, kind: "minus" },
    ...(w.chargeback_losses ? [{ key: "cb", label: "Chargebacks lost", amount: -w.chargeback_losses, kind: "minus" as const, hint: "Disputed amounts not won (the fees are in processor fees)" }] : []),
    { key: "contribution", label: "Contribution", amount: w.contribution, kind: "result" },
  ]
  // Bars on one scale from 0 to the largest running total (gross, or more if fees were negative).
  let run = 0
  const spans = rows.map((r) => {
    if (r.kind === "total") {
      run = r.amount
      return { from: 0, to: r.amount }
    }
    if (r.kind === "result") return { from: Math.min(0, r.amount), to: Math.max(0, r.amount) }
    const from = run
    run += r.amount
    return { from: Math.min(from, run), to: Math.max(from, run) }
  })
  const lo = Math.min(0, ...spans.map((s) => s.from))
  const hi = Math.max(1, ...spans.map((s) => s.to))
  const x = (v: number) => ((v - lo) / (hi - lo)) * 100
  const def = w.fee_source.profiles.find((p) => p.default)
  const trued = w.fee_source.trued_up_months
  const months = w.fee_source.months
  const monthName = (m: string) => new Date(`${m}-15T12:00:00Z`).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" })
  const warn = w.warnings
  return (
    <Card
      title={`Where ${data.window.range === "today" ? "today's" : "the"} money went`}
      right={<Segmented label="Show as" value={mode} onChange={setMode} options={[{ value: "usd", label: "$" }, { value: "pct", label: "% of gross" }]} />}
      className="h-full"
      footer={
        <div className="flex flex-col gap-1.5">
          <span>
            Fees from the {w.fee_source.default_profile} profile{def?.percent != null ? `: ${def.percent}% + ${money(def.fixed ?? 0)}/order` : ""}
            {def?.monthly_fixed ? ` + ${money(def.monthly_fixed, false)}/mo fixed, allocated daily` : ""}.{" "}
            {trued.length ? `${trued.map(monthName).join(" and ")} use${trued.length === 1 ? "s" : ""} the statement true-up${months.length > trued.length ? "; the rest are estimates until the statement arrives" : ""}.` : "Replaced by statement actuals after the monthly true-up."}{" "}
            <Link to="/analytics/costs/processing-fees" className="text-[var(--k-blue)] hover:underline">
              Fee profiles
            </Link>
          </span>
          {warn.lines_missing_cost > 0 && (
            <span className="text-[var(--tb-attention-fg)]" role="status">
              ⚠ {count(warn.lines_missing_cost)} order line{warn.lines_missing_cost === 1 ? " has" : "s have"} no landed cost ({money(warn.net_sales_missing_cost)} of net sales): COGS and contribution are overstated until{" "}
              <Link to="/analytics/costs" className="underline">
                costs are added
              </Link>
              .
            </span>
          )}
          {(warn.shipping_estimated_orders > 0 || warn.shipping_missing_orders > 0) && (
            <span>
              {warn.shipping_estimated_orders > 0 ? `Shipping is estimated for ${count(warn.shipping_estimated_orders)} order${warn.shipping_estimated_orders === 1 ? "" : "s"} (${money(warn.shipping_estimated_amount)}) until their labels are bought. ` : ""}
              {warn.shipping_missing_orders > 0 ? `${count(warn.shipping_missing_orders)} order${warn.shipping_missing_orders === 1 ? " has" : "s have"} no label or estimate yet (counted as $0).` : ""}
            </span>
          )}
          {w.shipping_charged > 0 && <span>Shipping charged to customers ({money(w.shipping_charged)}) isn't part of net sales.</span>}
        </div>
      }
    >
      <ul className="flex flex-col" data-testid="waterfall">
        {rows.map((r, i) => {
          const s = spans[i]
          const minus = r.kind === "minus" && r.amount !== 0 ? "−" : ""
          const val = mode === "usd" ? (r.kind === "minus" ? `${minus}${money(Math.abs(r.amount))}` : money(r.amount)) : g > 0 ? `${minus}${pct((100 * Math.abs(r.amount)) / g, 1)}` : "—"
          const bar = r.kind === "total" ? "var(--k-hull)" : r.kind === "result" ? (r.amount >= 0 ? "var(--k-brass)" : "var(--k-alert)") : "var(--k-bar-soft, var(--k-series-3))"
          return (
            <li key={r.key} className={`grid grid-cols-[minmax(0,9.5rem)_1fr_auto] items-center gap-3 py-[7px] ${r.kind === "result" ? "mt-1 border-t border-[var(--k-line)] pt-3" : ""}`} title={r.hint}>
              <span className={`truncate text-[14px] ${r.kind === "minus" ? "text-[var(--k-ink-muted)]" : "font-medium text-[var(--k-ink)]"}`}>{r.label}</span>
              <span className="relative h-3.5">
                {(r.amount !== 0 || r.kind !== "minus") && <span className="absolute inset-y-0 rounded-[3px]" style={{ left: `${x(s.from)}%`, width: `${Math.max(0.6, x(s.to) - x(s.from))}%`, background: bar }} />}
              </span>
              <span className={`k-data min-w-[84px] text-right text-[14px] ${r.kind === "result" ? "font-medium text-[var(--k-brass-ink)]" : r.kind === "minus" ? "text-[var(--k-ink-muted)]" : "text-[var(--k-ink)]"}`}>{val}</span>
            </li>
          )
        })}
      </ul>
      <div className="mt-1 flex justify-end">
        <span className="text-[13px] text-[var(--k-ink-muted)]">
          Net sales <span className="k-num text-[var(--k-ink)]">{money(w.net_sales)}</span> · contribution <span className="k-num text-[var(--k-brass-ink)]">{w.contribution_pct == null ? "—" : pct(w.contribution_pct, 1)}</span> of net
        </span>
      </div>
    </Card>
  )
}

// ── Payments health ──────────────────────────────────────────────────────────

function LimitEditor({ profile, current, onDone }: { profile: string; current: number | null; onDone: () => void }) {
  const save = useSaveChargebackLimit()
  const [v, setV] = useState(current == null ? "" : String(current))
  const n = Number(v)
  const ok = v.trim() === "" || (Number.isFinite(n) && n >= 0.01 && n <= 10)
  return (
    <span className="inline-flex items-center gap-1.5">
      <Input size="small" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} aria-label="Chargeback limit in percent" placeholder="0.90" className="h-7 w-[72px]" />
      <span className="text-[13px] text-[var(--k-ink-muted)]">%</span>
      <button
        type="button"
        disabled={!ok || save.isPending}
        className="text-[13px] font-medium text-[var(--k-blue)] hover:underline disabled:opacity-50"
        onClick={() => save.mutate({ profile, chargeback_limit_percent: v.trim() === "" ? null : n }, { onSuccess: () => (toast.success("Chargeback limit saved"), onDone()), onError: (e: any) => toast.error(e?.message ?? "Couldn't save") })}
      >
        Save
      </button>
      <button type="button" className="text-[13px] text-[var(--k-ink-muted)] hover:underline" onClick={onDone}>
        Cancel
      </button>
    </span>
  )
}

function ChargebackBar({ h, profileId, canWrite }: { h: MidHealth; profileId: string | null; canWrite: boolean }) {
  const [editing, setEditing] = useState(false)
  const cb = h.chargebacks
  const used = cb.used == null ? 0 : Math.min(cb.used, 1)
  const red = cb.level === "alert" || cb.level === "over"
  return (
    <div className="mt-3" data-testid="chargeback-bar">
      <div className="relative h-2 overflow-hidden rounded-full bg-[var(--k-sunken)] shadow-[inset_0_0_0_1px_var(--k-line)]">
        {cb.limit != null && <div className="h-full rounded-full transition-[width]" style={{ width: `${Math.max(used * 100, cb.count ? 2 : 0)}%`, background: red ? "var(--k-alert)" : "var(--k-positive)" }} />}
        {cb.limit != null && <span aria-hidden className="absolute inset-y-[-2px] w-px bg-[var(--k-ink-muted)]" style={{ left: "75%" }} />}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[13px] text-[var(--k-ink-muted)]">
        <span>Chargeback ratio vs limit</span>
        {editing && profileId ? (
          <LimitEditor profile={profileId} current={cb.limit} onDone={() => setEditing(false)} />
        ) : cb.limit != null ? (
          <span>
            <span className={red ? "font-medium text-[var(--k-alert)]" : ""}>{cb.used == null ? "—" : `${Math.round(cb.used * 100)}% of ${cb.limit}%`}</span> · alert at 75%
            {canWrite && profileId && (
              <button type="button" className="ml-1.5 text-[var(--k-blue)] hover:underline" onClick={() => setEditing(true)}>
                Edit
              </button>
            )}
          </span>
        ) : canWrite && profileId ? (
          <button type="button" className="font-medium text-[var(--k-blue)] hover:underline" onClick={() => setEditing(true)} data-testid="set-limit">
            Set limit
          </button>
        ) : (
          <span>No limit set for this MID</span>
        )}
      </div>
    </div>
  )
}

export function PaymentsHealth({ data, mid, onMid }: { data: HomeData; mid: string; onMid: (m: string) => void }) {
  const can = useCan()
  const p = data.payments
  const id = p.by_mid[mid] ? mid : "all"
  const h = p.by_mid[id]
  const profileId = id === "all" ? (p.mids.length === 2 ? p.mids[1].id : null) : id
  const cb = h.chargebacks
  return (
    <Card
      id="payments"
      title="Payments health"
      right={
        <Segmented
          label="Merchant account"
          value={id}
          onChange={onMid}
          options={[...p.mids.map((m) => ({ value: m.id, label: m.id === "all" ? "All MIDs" : m.name })), { value: "__maverick", label: "Maverick · soon", disabled: true, hint: "The second MID gets its own fee profile and tab once its account is live" }]}
        />
      }
      className="h-full"
      footer={
        <span>
          Settlement figures are Keel's own card payments; other storefronts on the same Authorize.net account are shown apart.{" "}
          <Link to="/analytics/settlements" className="text-[var(--k-blue)] hover:underline">
            Settlements
          </Link>
        </span>
      }
    >
      {h.settled && h.unsettled ? (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <div className="rounded-[var(--k-r-sm)] bg-[var(--k-sunken)] px-3.5 py-3 shadow-[inset_0_0_0_1px_var(--k-line)]">
            <div className="k-eyebrow">Settled</div>
            <div className="k-num mt-1 text-[22px] font-medium leading-7 text-[var(--k-ink)]">{money(h.settled.keel_net)}</div>
            <div className="mt-0.5 text-[13px] leading-5 text-[var(--k-ink-muted)]" data-testid="settled-label">
              {h.settled.label ?? "No batch settled in this period"}
            </div>
            {h.settled.other_net !== 0 && <div className="text-[13px] leading-5 text-[var(--k-ink-muted)]">+ {money(h.settled.other_net)} other storefronts</div>}
          </div>
          <div className="rounded-[var(--k-r-sm)] bg-[var(--k-sunken)] px-3.5 py-3 shadow-[inset_0_0_0_1px_var(--k-line)]">
            <div className="k-eyebrow">Unsettled</div>
            <div className="k-num mt-1 text-[22px] font-medium leading-7 text-[var(--k-ink)]">{money(h.unsettled.keel_net)}</div>
            <div className="mt-0.5 text-[13px] leading-5 text-[var(--k-ink-muted)]">{h.unsettled.count ? h.unsettled.label : "Nothing waiting for a batch"}</div>
            {h.unsettled.authorized > 0 && <div className="text-[13px] leading-5 text-[var(--k-ink-muted)]">+ {money(h.unsettled.authorized)} authorized, not captured</div>}
          </div>
        </div>
      ) : (
        <Muted className="rounded-[var(--k-r-sm)] bg-[var(--k-sunken)] px-3.5 py-3">Settlement data comes from the store's Authorize.net account; this MID has none synced yet.</Muted>
      )}
      <div className="mt-2 divide-y divide-[var(--k-line)]">
        <Row
          label="Approval rate"
          value={h.approval.rate == null ? "—" : pct(h.approval.rate, 1)}
          sub={data.window.compare !== "none" ? <Delta pts={h.approval.delta_pts} good="up" none={h.approval.no_baseline} hint={h.approval.no_baseline ? noBaselineHint(h.approval.no_baseline, data.window) : undefined} /> : undefined}
        />
        <Row label="Declined attempts" value={count(h.declines.count)} sub={`~${money(h.declines.cost)} in fees`} />
        <Row
          label={<span title={data.definitions.refunds.definition}>Refunds</span>}
          value={count(h.refunds.count)}
          sub={
            <span data-testid="payments-refunds">
              {money(h.refunds.amount)}
              {h.refunds.cancellations ? ` · incl. ${count(h.refunds.cancellations)} cancellation${h.refunds.cancellations === 1 ? "" : "s"}` : ""}
            </span>
          }
        />
        <Row
          label={`Chargebacks · ${cb.month_label}`}
          value={count(cb.count)}
          sub={cb.ratio == null ? "—" : `${cb.ratio.toFixed(2)}% of txns`}
          tone={cb.level === "alert" || cb.level === "over" ? "alert" : undefined}
          to={cb.open ? "/analytics/costs/processing-fees?chargebacks=open#chargebacks" : undefined}
        />
      </div>
      <ChargebackBar h={h} profileId={profileId} canWrite={can("analytics:write")} />
      {cb.account_wide && cb.account_wide.count > 0 && (
        <Muted className="mt-2">
          Authorize.net reports {count(cb.account_wide.count)} chargeback{cb.account_wide.count === 1 ? "" : "s"} account-wide this month ({cb.account_wide.ratio == null ? "—" : `${cb.account_wide.ratio.toFixed(2)}%`} of {count(cb.account_wide.transactions)} settled transactions, all storefronts).
        </Muted>
      )}
    </Card>
  )
}

// ── Subscriptions ────────────────────────────────────────────────────────────

export function Subscriptions({ data }: { data: HomeData }) {
  const s = data.subscriptions
  if (!s) return null
  const cmp = data.window.compare !== "none"
  const range = data.window.range === "today" ? "today" : data.window.range === "yesterday" ? "yesterday" : "in period"
  return (
    <Card title="Subscriptions" right={<MoreLink to="/apps/subscriptions">Open</MoreLink>} className="h-full" footer="Each retry costs ~$0.55 whether it clears or not. The retry schedule lives in Subscriptions → Settings.">
      <div className="grid grid-cols-3 gap-3" data-testid="subscriptions">
        <div className="min-w-0">
          <div className="k-eyebrow" title={s.definitions.active_subscriptions.definition}>
            Active subscriptions
          </div>
          <div className="k-num mt-1 text-[22px] font-medium leading-7 text-[var(--k-ink)]" title={s.definitions.active_subscriptions.definition} data-testid="active-subscriptions">
            {count(s.active_subscriptions)}
          </div>
          <div className="text-[13px] leading-5 text-[var(--k-ink-muted)]">
            <span title="New − cancelled + reactivated (and imports) over the period" className={s.subscriptions_net_change > 0 ? "text-[var(--k-positive)]" : s.subscriptions_net_change < 0 ? "text-[var(--k-alert)]" : ""}>
              {s.subscriptions_net_change > 0 ? "+" : s.subscriptions_net_change < 0 ? "−" : "±"}
              {count(Math.abs(s.subscriptions_net_change))} net
            </span>{" "}
            ·{" "}
            <span title={s.definitions.subscription_customers.definition} className="cursor-help underline decoration-dotted underline-offset-2" data-testid="subscription-customers">
              {count(s.active_subscribers)} customer{s.active_subscribers === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        <div className="min-w-0">
          <div className="k-eyebrow">MRR</div>
          <div className="k-num mt-1 text-[22px] font-medium leading-7 text-[var(--k-ink)]">{money(s.mrr, false)}</div>
          <div className="text-[13px] leading-5">{cmp ? <Delta pct={s.mrr_delta.pct} good="up" none={s.mrr_no_baseline} hint={s.mrr_no_baseline ? noBaselineHint(s.mrr_no_baseline, data.window) : undefined} /> : <span className="text-[var(--k-ink-muted)]">—</span>}</div>
        </div>
        <div className="min-w-0">
          <div className="k-eyebrow">Renewal success</div>
          <div className="k-num mt-1 text-[22px] font-medium leading-7 text-[var(--k-ink)]" title={`${s.renewal.paid} of ${s.renewal.attempted} renewals attempted were paid (retries count once per renewal)`}>
            {s.renewal.rate == null ? "—" : pct(s.renewal.rate, 1)}
          </div>
          <div className="text-[13px] leading-5">{cmp ? <Delta pts={s.renewal.delta_pts} good="up" none={s.renewal.no_baseline} hint={s.renewal.no_baseline ? noBaselineHint(s.renewal.no_baseline, data.window) : undefined} /> : <span className="text-[var(--k-ink-muted)]">—</span>}</div>
        </div>
      </div>
      <div className="mt-3 divide-y divide-[var(--k-line)] border-t border-[var(--k-line)]">
        <Row label={`New ${range}`} value={count(s.new)} />
        <Row label={`Cancelled ${range}`} value={count(s.cancelled)} />
        <Row label={`Renewals paid ${range}`} value={`${count(s.renewal.paid)} of ${count(s.renewal.attempted)}`} sub={s.renewal.attempted ? "attempted" : undefined} />
        {s.reactivated > 0 && <Row label={`Reactivated ${range}`} value={count(s.reactivated)} />}
        <Row label="Failed renewals awaiting retry" value={count(s.retrying.count)} sub={s.retrying.count ? money(s.retrying.amount) : undefined} to="/apps/subscriptions/payments?status=retrying" tone={s.retrying.count ? "alert" : undefined} />
      </div>
    </Card>
  )
}

// ── Needs attention ──────────────────────────────────────────────────────────

export function Attention({ data, className = "" }: { data: HomeData; className?: string }) {
  const can = useCan()
  const items = data.attention.filter((i) => can(i.perm as any))
  return (
    <Card
      title="Needs attention"
      right={
        items.length ? (
          <span className="rounded-[var(--k-r-sm)] bg-[var(--tb-critical-bg)] px-2 py-[2px] text-[13px] font-medium text-[var(--tb-critical-fg)]">
            {items.length} item{items.length === 1 ? "" : "s"}
          </span>
        ) : null
      }
      className={className}
    >
      {!items.length ? (
        <div className="flex items-center gap-3 rounded-[var(--k-r-sm)] bg-[var(--tb-success-bg)] px-3.5 py-3 text-[14px] text-[var(--tb-success-fg)]" data-testid="attention-empty">
          <span aria-hidden>✓</span> Nothing is waiting on you right now.
        </div>
      ) : (
        <ul className="-mx-2 flex flex-col" data-testid="attention">
          {items.map((i) => {
            const urgent = i.tier <= 1
            return (
              <li key={i.key} className="border-b border-[var(--k-line)] last:border-0">
                <Link to={i.to} className="flex items-center justify-between gap-3 rounded-[var(--k-r-sm)] px-2 py-2.5 hover:bg-[var(--k-blue-50)]" data-testid={`attention-${i.key}`}>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-medium text-[var(--k-ink)]">{i.title}</span>
                    <span className="block truncate text-[13px] leading-5 text-[var(--k-ink-muted)]">{i.detail}</span>
                  </span>
                  <span className={`flex shrink-0 items-center gap-1.5 text-[13px] ${urgent ? "font-medium text-[var(--k-alert)]" : i.tier === 2 ? "text-[var(--tb-attention-fg)]" : "text-[var(--k-ink-muted)]"}`}>
                    {i.chip && <span className="k-num">{i.chip}</span>}
                    <span aria-hidden className="text-[16px] leading-none text-[var(--k-ink-muted)]">›</span>
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// ── Traffic and sales by source ──────────────────────────────────────────────

type SortKey = "label" | "sessions" | "orders" | "net_sales" | "cvr" | "contribution"
export function Acquisition({ data }: { data: HomeData }) {
  const [by, setBy] = useState<"source" | "utm">("source")
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: "net_sales", dir: -1 })
  const rows = useMemo(() => {
    const list = [...data.acquisition[by]]
    return list.sort((a, b) => {
      const av = a[sort.k] ?? -Infinity
      const bv = b[sort.k] ?? -Infinity
      return (typeof av === "string" ? String(av).localeCompare(String(bv)) : (av as number) - (bv as number)) * sort.dir
    })
  }, [data, by, sort])
  const t = data.acquisition.total[by]
  const head = (k: SortKey, label: string, right = true) => (
    <th scope="col" aria-sort={sort.k === k ? (sort.dir === 1 ? "ascending" : "descending") : "none"} className={`px-3 py-2 font-medium first:pl-4 last:pr-4 ${right ? "text-right" : "text-left"}`}>
      <button type="button" className="k-eyebrow inline-flex items-center gap-1 hover:text-[var(--k-ink)]" onClick={() => setSort((s) => ({ k, dir: s.k === k ? (s.dir === 1 ? -1 : 1) : k === "label" ? 1 : -1 }))}>
        {label}
        {sort.k === k && <span aria-hidden>{sort.dir === 1 ? "↑" : "↓"}</span>}
      </button>
    </th>
  )
  const cell = (r: SourceRow) => (
    <>
      <td className="k-data px-3 py-2.5 text-right">{r.no_visit ? <span className="text-[var(--k-ink-muted)]">—</span> : count(r.sessions)}</td>
      <td className="k-data px-3 py-2.5 text-right">{count(r.orders)}</td>
      <td className="k-data px-3 py-2.5 text-right">{money(r.net_sales)}</td>
      <td className="k-data px-3 py-2.5 text-right">{r.cvr == null ? <span className="text-[var(--k-ink-muted)]">—</span> : pct(r.cvr, 1)}</td>
      <td className={`k-data px-3 py-2.5 pr-4 text-right ${r.contribution < 0 ? "text-[var(--k-alert)]" : ""}`}>{money(r.contribution)}</td>
    </>
  )
  return (
    <Card
      title="Traffic and sales by source"
      right={
        <div className="flex items-center gap-3">
          <Segmented label="Group by" value={by} onChange={setBy} options={[{ value: "source", label: "Source" }, { value: "utm", label: "UTM" }]} />
          <MoreLink to="/analytics/reports">Open in Analytics</MoreLink>
        </div>
      }
      footer={`An order belongs to the session it was placed in, or, when that visit was Direct, to the same shopper's latest visit with a source in the 7 days before. Contribution per source takes each order's own costs; costs tied to no order (monthly fixed fees, card checks, true-ups: ${money(data.acquisition.store_wide_costs)}) have their own row, so the total matches the tiles above. Renewals and draft orders have no storefront visit.`}
    >
      <div className="-mx-4 overflow-x-auto sm:-mx-5">
        <table className="w-full min-w-[640px] text-[14px]" data-testid="acquisition">
          <thead>
            <tr className="border-y border-[var(--k-line)]">
              {head("label", by === "source" ? "Source" : "UTM source / medium", false)}
              {head("sessions", "Sessions")}
              {head("orders", "Orders")}
              {head("net_sales", "Net sales")}
              {head("cvr", "CVR")}
              {head("contribution", "Contribution")}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--k-line)]">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[13px] text-[var(--k-ink-muted)]">
                  No sessions or orders in this period
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="max-w-[260px] truncate px-3 py-2.5 pl-4 text-[var(--k-ink)]">{r.label}</td>
                {cell(r)}
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-[var(--k-line)] font-medium">
                <td className="px-3 py-2.5 pl-4">Total</td>
                {cell({ key: "total", label: "Total", sessions: t.sessions, orders: t.orders, net_sales: t.net_sales, contribution: t.contribution, cvr: data.kpis.conversion.value })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  )
}

// ── Recent orders ────────────────────────────────────────────────────────────

export function RecentOrders({ data }: { data: HomeData }) {
  const tz = data.store.timezone
  const sameDay = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso)) === new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date())
  return (
    <Card title="Recent orders" right={<MoreLink to="/orders">View all</MoreLink>}>
      {!data.recent_orders.length ? (
        <Muted className="py-6">No orders yet. Orders placed on your store appear here.</Muted>
      ) : (
        <ul className="-mx-2 flex flex-col" data-testid="recent-orders">
          {data.recent_orders.map((o) => (
            <li key={o.id} className="border-b border-[var(--k-line)] last:border-0">
              <Link to={`/orders/${o.id}`} className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-1 rounded-[var(--k-r-sm)] px-2 py-2.5 hover:bg-[var(--k-blue-50)] md:grid-cols-[5.5rem_minmax(0,10rem)_1fr_6.5rem_6rem]">
                <span className="k-data font-medium text-[var(--k-ink)]">{o.name}</span>
                <span className="truncate text-[14px] text-[var(--k-ink)]">{shortName(o.customer_name) ?? o.email ?? "Guest"}</span>
                <span className="k-data text-right text-[var(--k-ink)] md:order-last">{money(o.total)}</span>
                <span className="col-span-2 flex flex-wrap gap-1.5 md:col-span-1">
                  <StatusBadge b={o.payment} />
                  <StatusBadge b={o.fulfillment} />
                </span>
                <span className="k-data text-right text-[var(--k-ink-muted)]">{sameDay(o.placed_at) ? timeOfDay(o.placed_at, tz) : `${dayLabel(new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(o.placed_at)))}`}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
